import { Box, Typography, Grid } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { Link } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { uiNeutrals } from "../../constants/uiNeutrals";
import RestaurantItemImage from "../restaurant/RestaurantItemImage";
import DealTags from "./DealTags";
import { formatDisplayDate } from "../../utils/formatDisplayDate";
import { isDealExpired } from "./dealList/utils/calendarDealBuckets";
import { motion } from "framer-motion";

const DealCard = ({
  deal,
  isMobile = false,
  gridProps = {},
  onExpand = null,
  useExpansion = false,
  imageLoading = "lazy", // "lazy" | "eager" - for above-the-fold images
}) => {
  const defaultGridProps = isMobile
    ? { item: true, xs: 6, sm: 6, md: 4 }
    : { item: true, xs: 6, sm: 6, md: 4 };

  const mergedGridProps = { ...defaultGridProps, ...gridProps };
  const isExpired = isDealExpired(deal);
  const restaurantName = deal.brand?.name || "Brand";

  const handleDealClick = (e) => {
    if (useExpansion && onExpand) {
      // Use expansion mode (Little Red Book style)
      e.preventDefault();
      onExpand(deal);
    } else {
      // Use navigation mode (original behavior)
      // Save scroll position and current URL before navigating
      try {
        sessionStorage.setItem(
          "deals_scroll_position",
          window.scrollY.toString(),
        );
        // Save the current URL with all filters
        sessionStorage.setItem(
          "deals_previous_url",
          window.location.pathname + window.location.search,
        );
      } catch (error) {
        // Ignore errors
      }
    }
  };

  const CardWrapper = useExpansion ? Box : Link;
  const wrapperProps = useExpansion
    ? { onClick: handleDealClick, sx: { cursor: "pointer" } }
    : {
        to: `/deal/${deal.shortId || deal._id}`,
        style: { textDecoration: "none" },
        onClick: handleDealClick,
      };

  return (
    <Grid {...mergedGridProps} key={deal._id}>
      <CardWrapper {...wrapperProps}>
        <Box
          sx={{
            overflow: "hidden",
            opacity: isExpired ? 0.58 : 1,
            filter: isExpired ? "grayscale(35%)" : "none",
            transition: "opacity 0.2s ease, filter 0.2s ease",
            pb: 2,
            borderBottom: `1px solid ${uiNeutrals.rowDivider}`,
          }}
        >
          <Box
            component={useExpansion ? Box : motion.div}
            {...(!useExpansion && {
              layoutId: `deal-image-${deal.shortId || deal._id}`,
            })}
            sx={{
              width: "100%",
              height: "auto",
              aspectRatio: "1/1",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              // border: "1px solid #e0e0e0",
              // borderRadius: "6px",
              overflow: "hidden",
              cursor: "pointer",
              "&:hover": {
                "& .restaurant-img": {
                  transform: "scale(1.05)",
                },
                "& .arrow-icon": {
                  opacity: 1,
                  transform: "translate(-50%, -50%) scale(1)",
                },
              },
            }}
          >
            <RestaurantItemImage
              restaurantName={deal.brand?.name || "Brand"}
              imageUrl={deal.imageUrl}
              useDefaultFallback={false}
              size="100%"
              className="restaurant-img"
              loading={imageLoading}
              sx={{
                width: "100%",
                height: "100%",
                transition: "transform 0.3s ease",
              }}
            />

            {/* Deal Summary badge */}
            {deal.dealSummary && (
              <Box
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  backgroundColor: "white",
                  color: "black",
                  borderRadius: "25px",
                  px: 1.5,
                  py: 0.5,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: "500",
                    fontSize: isMobile
                      ? { xs: "12px", md: "15px" }
                      : { sx: "14px", md: "16px" },
                  }}
                >
                  {deal.dealSummary}
                </Typography>
              </Box>
            )}

            {/* Discount/Price/Label badge */}
            {deal?.category === "birthday" ||
            deal?.category === "double_11" ||
            typeof deal.discount_price === "number" ||
            (deal.discount_label && deal.discount_label.trim() !== "") ||
            (deal.discount &&
              (typeof deal.discount.value === "number" ||
                deal.discount.type === "bxfy" ||
                deal.discount.type === "label")) ||
            (deal.bxfy &&
              typeof deal.bxfy.buy === "number" &&
              typeof deal.bxfy.free === "number") ? (
              <Box
                sx={{
                  position: "absolute",
                  top: 8,
                  right: deal.dealSummary ? 10 : 8,
                  left: deal.dealSummary ? 10 : "auto",
                  backgroundColor: "#e3f2fd",
                  color: "#1976d2",
                  borderRadius: "20px",
                  px: 1.5,
                  py: 0.5,
                  zIndex: 1,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: "600",
                    fontSize: isMobile
                      ? { xs: "13px", md: "15px" }
                      : { xs: "12px", md: "14px" },
                  }}
                >
                  {(() => {
                    // Priority: special events -> discount_label -> discount_price -> discount object
                    if (deal?.category === "double_11") {
                      return (
                        (deal.discount_label && deal.discount_label.trim()) ||
                        "11.11 Special"
                      );
                    }
                    if (deal?.category === "birthday") {
                      return (
                        (deal.discount_label && deal.discount_label.trim()) ||
                        "Birthday Special"
                      );
                    }
                    if (deal?.category === "black_friday") {
                      return (
                        (deal.discount_label && deal.discount_label.trim()) ||
                        "Black Friday Special"
                      );
                    }

                    // Legacy fields
                    if (typeof deal.discount_price === "number") {
                      return `RM ${Number(deal.discount_price).toFixed(2)}`;
                    }
                    if (
                      deal.discount_label &&
                      deal.discount_label.trim() !== ""
                    ) {
                      return deal.discount_label;
                    }

                    // New unified discount object
                    if (deal.discount) {
                      const { type, value, buy, free, label } = deal.discount;

                      if (type === "bxfy" && buy != null && free != null) {
                        return `Buy ${Number(buy)} Free ${Number(free)}`;
                      }

                      if (
                        type === "label" &&
                        typeof label === "string" &&
                        label.trim() !== ""
                      ) {
                        return label.trim();
                      }

                      if (typeof value === "number") {
                        return type === "percentage"
                          ? `${Number(value)}% off`
                          : `RM ${Number(value).toFixed(2)}`;
                      }
                    }

                    // Legacy bxfy field (when discount not populated with bxfy)
                    if (
                      deal.bxfy &&
                      typeof deal.bxfy.buy === "number" &&
                      typeof deal.bxfy.free === "number"
                    ) {
                      return `Buy ${Number(deal.bxfy.buy)} Free ${Number(deal.bxfy.free)}`;
                    }

                    return null;
                  })()}
                </Typography>
              </Box>
            ) : null}

            <Box
              className="arrow-icon"
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%) scale(0.8)",
                opacity: 0,
                transition: "all 0.3s ease",
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                borderRadius: "50%",
                width: "50px",
                height: "50px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ArrowForwardIcon sx={{ fontSize: "20px", color: "#000" }} />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              py: 1,
              mt: isMobile ? 0.5 : 1,
              gap: 0.5,
              color: "black",
            }}
          >
            {/* Restaurant name */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                // mt: 0.5,
                cursor: "pointer",
                "&:hover": {
                  opacity: 0.7,
                },
              }}
              // onClick={handleRestaurantClick}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "#666666",
                  fontSize: isMobile
                    ? { xs: "13px", md: "15px" }
                    : { xs: "12px", md: "14px" },
                  fontWeight: 500,
                }}
              >
                {restaurantName}
              </Typography>
            </Box>

            {/* Deal title */}
            <Box sx={{ width: "100%" }}>
              <Typography
                variant="h6"
                sx={{
                  color: "#1a1a1a",
                  fontSize: isMobile
                    ? { xs: "16px", md: "18px" }
                    : { xs: "16px", md: "16px" },
                  fontWeight: 500,
                }}
              >
                {deal.title}
              </Typography>
            </Box>

            {/* Tags */}
            <DealTags
              fulfillment={deal.fulfillment}
              timeOfDay={deal.time_of_day}
            />

            {/* Posted / Updated date */}
            {(deal.postedOn || deal.createdAt || deal.updatedAt) && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  pt: 0.75,
                }}
              >
                <ArrowUpwardIcon sx={{ fontSize: 14, color: "#666" }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: "#666",
                    fontSize: isMobile ? "12px" : "12px",
                    lineHeight: 1,
                  }}
                >
                  {deal.updatedAt
                    ? formatDisplayDate(deal.updatedAt)
                    : `Posted ${formatDisplayDate(
                        deal.postedOn || deal.createdAt,
                      )}`}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </CardWrapper>
    </Grid>
  );
};

export default DealCard;
